// Accessibility issues fixed by AI
/* eslint-disable react/sort-comp */
import classnames from 'classnames';
import React, { PureComponent } from 'react';
import _ from 'underscore';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import {
  WithManagedFocuser,
  IManagedFocuserInjectedProps,
} from '@bingads-webui-react/hoc-with-managed-focuser';
import { OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import {
  withOverflowProp,
  IWithOverflowInjectedProps,
} from '@bingads-webui-react/with-overflow-prop';
import { withSignal, CommonEventParser } from '@bingads-webui-react/hoc-with-signal';
import { ItemAriaRoleType } from './aria-role-config';
import type { IRawListItemProps } from './types';

type OverflowManagedListItemProps =
  Omit<IRawListItemProps, keyof IManagedFocuserInjectedProps | keyof IWithOverflowInjectedProps>
  & IWithOverflowInjectedProps;
type OverflowListItemProps = OverflowManagedListItemProps & IManagedFocuserInjectedProps;

class RawListItem extends PureComponent<IRawListItemProps> {
  static defaultProps: Partial<IRawListItemProps> = {
    onSelect: _.noop,
    selectedItem: undefined,
    focusableRef: null,
    focusThis: _.noop,
    focusItemCallback: _.noop,
    forwardedRef: null,
    isTextOverflow: false,
  };

  constructor(props: IRawListItemProps) {
    super(props);

    this.onFocusHandler = this.onFocusHandler.bind(this);
    this.onSelected = this.onSelected.bind(this);
  }

  onSelected(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this.props.item.disabled) {
      return;
    }
    this.props.onSelect(this.props.item, this.props.index, event);
    this.props.focusThis();
  }

  onFocusHandler() {
    this.props.focusItemCallback(this.props.index);
  }

  renderOriginalItem(forwardedRef?: React.RefObject<HTMLElement> | null): React.ReactNode {
    const {
      item,
      index,
      keyValue,
      checkSelected,
      selectedItem,
      renderItem,
      focusableRef,
      ariaRole,
      listSize,
      positionInList,
      isFocused,
    } = this.props;

    const isSelected = checkSelected(selectedItem, item);

    const ariaAttributes: { [key: string]: string | boolean } = {};

    if (ariaRole === ItemAriaRoleType.ListBoxOption) {
      ariaAttributes['aria-selected'] = isSelected;
    } else if (ariaRole === ItemAriaRoleType.ListItem) {
      ariaAttributes['aria-setsize'] = `${listSize}`;
      ariaAttributes['aria-posinset'] = `${positionInList}`;

      if (isSelected) {
        ariaAttributes['aria-current'] = 'page';
      }
    }

    // Determine tabIndex: focused item or first item should be tabbable
    const shouldBeTabbable = isFocused || index === 0;
    const tabIndexValue = shouldBeTabbable ? 0 : -1;

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div
        ref={focusableRef}
        role={ariaRole}
        {...ariaAttributes}
        key={keyValue}
        onClick={this.onSelected}
        // we need to bubble up event.
        onKeyDown={event => submitEvent(this.onSelected)(event, event)}
        tabIndex={tabIndexValue}
        className={classnames('list-selector-item', {
          selected: isSelected,
          disabled: item.disabled,
        })}
        onFocus={this.onFocusHandler}
      >
        {forwardedRef ? renderItem(item, index, forwardedRef) : renderItem(item, index)}
      </div>
    );
  }

  renderItemWithToolTip(): React.ReactNode {
    const {
      item,
      isTextOverflow,
      forwardedRef,
    } = this.props;
    return (
      <OverlayTrigger
        key="top"
        placement="top"
        overlay={(
          <Tooltip id="tooltip-top" className={isTextOverflow ? '' : 'hide'}>
            {isTextOverflow && item.name}
          </Tooltip>
        )}
      >
        {this.renderOriginalItem(forwardedRef)}
      </OverlayTrigger>
    );
  }

  render(): React.ReactNode {
    const { forwardedRef } = this.props;
    return forwardedRef ? this.renderItemWithToolTip() : this.renderOriginalItem();
  }
}

// onSelect sends three params this.props.item, this.props.index, event
// the 3rd one is what we're looking for
// compose a new array with 3rd element and send to CommonEventParser
const onSelectEventParser = (e: [unknown, unknown, React.SyntheticEvent]) => CommonEventParser([e[2]]);

export const ListItem = WithManagedFocuser(withSignal(RawListItem, {
  onSelect: onSelectEventParser,
}));

const OverflowListItem = RawListItem as React.ComponentType<OverflowListItemProps>;
const ManagedOverflowListItem = WithManagedFocuser<OverflowManagedListItemProps>(OverflowListItem);
export const ListItemWithToolTip = withOverflowProp(ManagedOverflowListItem);
